import { useMemo, useState } from 'react';
import type { SettlementPreview, SettlementRow } from '@dru/types';
import { BILLABLE_EVENT_LABEL, SETTLEMENT_STATUS_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, ConfirmDialog, DataTable, DetailSection, Dialog, InfoTip, Input, Select, StatusBadge, TextField, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import { holds } from '../../../layouts/ConsoleShell';
import { LabStatementTable } from './LabStatementTable';

/**
 * What one payee is owed, and what has been paid against it.
 *
 * The settlement API has existed since the payout work and had no screen on
 * the platform side: a partner's rates could be set here but the statements
 * those rates produced could only be seen from the partner's own portal. So
 * "what do we owe this lab" was a question the admin console could not
 * answer, which is the gap this closes.
 *
 * The order of the actions is the order the money moves, and each step is
 * deliberately separate:
 *
 *   preview → close (freeze) → issue (send) → record payment
 *
 * Preview changes nothing and can be run as often as somebody likes. Close
 * freezes the figures, and is the step that claims the underlying events so
 * the same signature cannot be paid twice. Issue sends it. Only then does
 * money move. Void exists because a sent statement is never deleted.
 *
 * Shared by the partner and physician pages: the same ledger, read for a
 * different kind of payee, so neither page grows its own idea of what a
 * settlement is.
 *
 * Each step is offered only to a reader holding the permission the API
 * checks for it (close, issue, record, reverse), so a read-only reader sees
 * the statements and is never handed a button that answers 403.
 */

function useSettlementRights() {
  const { permissions } = useAuth();
  return {
    close: holds(permissions, 'settlement.close'),
    issue: holds(permissions, 'settlement.issue'),
    record: holds(permissions, 'settlement.record'),
    reverse: holds(permissions, 'settlement.reverse'),
  };
}

const STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  ISSUED: 'pending',
  PARTIALLY_PAID: 'warning',
  PAID: 'covered',
  VOID: 'neutral',
};

/** The first and last day of the month containing `d`, as YYYY-MM-DD. */
function monthOf(d: Date) {
  const from = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  const to = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function PayeeSettlements({ payeeType, payeeId }: {
  payeeType: 'PARTNER' | 'PHYSICIAN'; payeeId: string;
}) {
  const settlements = useQuery<{ items: SettlementRow[] }>(
    () => api.get(`/settlements?payeeType=${payeeType}&payeeId=${payeeId}&limit=100`),
    [payeeType, payeeId],
  );
  const [closing, setClosing] = useState(false);
  const [open, setOpen] = useState<SettlementRow | null>(null);
  const may = useSettlementRights();

  const rows = settlements.data?.items ?? [];

  // What is still owed across every live statement. Void rows are excluded:
  // a withdrawn statement is not a debt.
  const outstanding = useMemo(
    () => rows.filter((s) => s.status !== 'VOID').reduce((n, s) => n + s.outstandingPaise, 0),
    [rows],
  );

  return (
    <div className="space-y-4">
      <DetailSection
        title="Statements"
        action={may.close && rows.length > 0 ? <Button size="sm" onClick={() => setClosing(true)}>Close a period</Button> : undefined}
      >
        <div className="flex justify-end">
          <InfoTip label="About statements">
            Each statement freezes one period of completed work at the rates in force on the day it happened.
            Closing a period claims the events behind it, so the same work cannot be settled twice.
          </InfoTip>
        </div>

        {outstanding > 0 && (
          <p className="mt-3 text-small text-neutral-700">
            Owed across open statements: <span className="tabular-nums font-semibold text-neutral-900">{formatPaise(outstanding)}</span>
          </p>
        )}

        <div className="mt-3">
          <DataTable<SettlementRow>
            rows={rows}
            loading={settlements.loading}
            error={settlements.error}
            onRetry={settlements.reload}
            rowKey={(s) => s.id}
            onRowClick={(s) => setOpen(s)}
            empty={{
              title: 'No statements yet',
              hint: may.close
                ? 'Close a period once there is completed work to settle. The preview shows the figures before anything is frozen.'
                : 'A statement appears here once finance closes a period of completed work.',
              action: may.close ? <Button onClick={() => setClosing(true)}>Close a period</Button> : undefined,
            }}
            columns={[
              { key: 'ref', header: 'Statement', alwaysVisible: true, cell: (s) => (
                <div>
                  <div className="font-mono text-xs text-neutral-900">{s.reference}</div>
                  <div className="text-xs text-neutral-500">
                    {formatDate(s.periodStart)} to {formatDate(s.periodEnd)}
                  </div>
                </div>
              ) },
              { key: 'events', header: 'Work settled', align: 'right', cell: (s) => (
                <span className="tabular-nums text-xs text-neutral-700">
                  {s.sourceCount}
                  {s.unpricedCount > 0 && (
                    <span className="text-warning-600" title="Events no published rate card priced">
                      {' '}+{s.unpricedCount} unpriced
                    </span>
                  )}
                </span>
              ) },
              { key: 'amount', header: 'Amount', align: 'right', cell: (s) => (
                <span className="tabular-nums text-sm text-neutral-900">{formatPaise(s.subtotalPaise, s.currency)}</span>
              ) },
              { key: 'out', header: 'Outstanding', align: 'right', cell: (s) => (
                <span className={`tabular-nums text-sm ${s.outstandingPaise > 0 ? 'font-semibold text-neutral-900' : 'text-neutral-500'}`}>
                  {formatPaise(s.outstandingPaise, s.currency)}
                </span>
              ) },
              { key: 'status', header: 'Status', mobileLabel: false, cell: (s) => (
                <StatusBadge tone={STATUS_TONE[s.status] ?? 'neutral'}>
                  {SETTLEMENT_STATUS_LABEL[s.status] ?? s.status}
                </StatusBadge>
              ) },
            ]}
          />
        </div>
      </DetailSection>

      {closing && (
        <ClosePeriodDialog
          payeeType={payeeType} payeeId={payeeId}
          onClose={() => setClosing(false)}
          onDone={() => { setClosing(false); settlements.reload(); }}
        />
      )}
      {open && (
        <SettlementDialog
          id={open.id}
          onClose={() => setOpen(null)}
          onChanged={() => settlements.reload()}
          may={may}
        />
      )}
    </div>
  );
}

/**
 * Preview, then freeze.
 *
 * The preview is not decoration: closing is the irreversible half, and the
 * figures it will freeze are worth seeing first. Unpriced events are shown
 * prominently because they are the one thing that silently shortens a
 * statement — the fix is a rate card, not a retry.
 */
function ClosePeriodDialog({ payeeType, payeeId, onClose, onDone }: {
  payeeType: 'PARTNER' | 'PHYSICIAN'; payeeId: string; onClose: () => void; onDone: () => void;
}) {
  const initial = monthOf(new Date());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && to >= from;

  const preview = useQuery<SettlementPreview>(
    () => (valid
      ? api.get(`/settlements/preview?payeeType=${payeeType}&payeeId=${payeeId}&from=${from}&to=${to}`)
      : Promise.resolve(null as never)),
    [payeeType, payeeId, from, to, valid],
  );

  const p = preview.data;
  const nothing = !!p && p.lines.length === 0;

  async function close() {
    setBusy(true); setError(null);
    try {
      await api.post('/settlements/close', {
        payeeType, payeeId, from, to, notes: notes.trim() || undefined,
      }, crypto.randomUUID());
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title="Close a settlement period" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-neutral-500">
          Closing freezes these figures and claims the work behind them. It cannot be edited afterwards —
          a mistake is corrected by voiding the statement and closing the period again.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Period from" type="date" value={from} onChange={setFrom} required />
          <TextField
            label="Period to" type="date" value={to} onChange={setTo} required
            error={!valid ? 'The period ends before it starts.' : null}
          />
        </div>

        <div className="rounded-input border border-neutral-200 p-3">
          <div className="text-small font-medium text-neutral-800">What this period would settle at</div>
          {preview.loading && <p className="mt-2 text-small text-neutral-500">Working it out…</p>}
          {preview.error && <Alert className="mt-2">{preview.error}</Alert>}
          {nothing && (
            <p className="mt-2 text-small text-neutral-500">
              No priced work in this period. Nothing to settle.
            </p>
          )}
          {p && p.lines.length > 0 && payeeType === 'PARTNER' && (
            <div className="mt-2">
              <LabStatementTable lines={p.lines} summary={p.summary} />
              <p className="mt-2 text-caption text-neutral-500">{p.sourceCount} events. A profile is charged once for its tests; a covered test is not charged again unless the agreement says so.</p>
            </div>
          )}
          {p && p.lines.length > 0 && payeeType !== 'PARTNER' && (
            <>
              <table className="mt-2 w-full text-sm">
                <thead className="text-left text-caption font-medium uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="py-1">What</th>
                    <th className="py-1 text-right">Qty</th>
                    <th className="py-1 text-right">Rate</th>
                    <th className="py-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {p.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="py-1.5">
                        <span className="text-neutral-900">{l.label}</span>
                        <span className="block text-caption text-neutral-500">
                          {BILLABLE_EVENT_LABEL[l.event] ?? l.event}
                        </span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{l.quantity}</td>
                      <td className="py-1.5 text-right tabular-nums">{formatPaise(l.ratePaise)}</td>
                      <td className="py-1.5 text-right tabular-nums">{formatPaise(l.amountPaise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 flex justify-between border-t border-neutral-200 pt-2 text-sm">
                <span className="font-medium text-neutral-800">Total · {p.sourceCount} events</span>
                <span className="tabular-nums font-semibold text-neutral-900">{formatPaise(p.subtotalPaise)}</span>
              </div>
            </>
          )}
          {p && p.unpricedCount > 0 && (
            <Alert tone="warning" className="mt-2">
              {p.unpricedCount} event{p.unpricedCount === 1 ? '' : 's'} in this period {p.unpricedCount === 1 ? 'has' : 'have'} no
              published rate card pricing {p.unpricedCount === 1 ? 'it' : 'them'}. {p.unpricedCount === 1 ? 'It' : 'They'} will be
              left out of the statement rather than settled at zero. Publish a rate card covering this period first.
            </Alert>
          )}
        </div>

        <TextField label="Notes (optional)" value={notes} onChange={setNotes} placeholder="Agreement or PO reference" />

        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={close} disabled={!valid || busy || nothing} loading={busy}>
            Close and freeze period
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** One statement in full: what it pays for, what has been paid, what is left. */
function SettlementDialog({ id, onClose, onChanged, may }: {
  id: string; onClose: () => void; onChanged: () => void;
  may: ReturnType<typeof useSettlementRights>;
}) {
  const statement = useQuery<SettlementRow>(() => api.get(`/settlements/${id}`), [id]);
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [issuing, setIssuing] = useState(false);
  // Every step happens in its own dialog, with its own busy state and its
  // own error; this one only reads.

  const s = statement.data;

  return (
    <Dialog title={s ? `Statement ${s.reference}` : 'Statement'} onClose={onClose} wide>
      {statement.loading && <p className="text-small text-neutral-500">Loading…</p>}
      {statement.error && <Alert>{statement.error}</Alert>}
      {s && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={STATUS_TONE[s.status] ?? 'neutral'}>
              {SETTLEMENT_STATUS_LABEL[s.status] ?? s.status}
            </StatusBadge>
            <span className="text-small text-neutral-600">
              {formatDate(s.periodStart)} to {formatDate(s.periodEnd)} · {s.sourceCount} events
            </span>
          </div>

          {s.status === 'VOID' && s.voidReason && (
            <Alert tone="warning">Withdrawn: {s.voidReason}</Alert>
          )}
          {s.unpricedCount > 0 && (
            <Alert tone="warning">
              {s.unpricedCount} event{s.unpricedCount === 1 ? '' : 's'} in this period went unpriced and
              {s.unpricedCount === 1 ? ' is' : ' are'} not included below.
            </Alert>
          )}

          {s.payeeType === 'PARTNER' && <LabStatementTable lines={s.lines ?? []} summary={s.summary} currency={s.currency} />}
          {s.payeeType !== 'PARTNER' && (
          <div className="overflow-x-auto rounded border border-neutral-100">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-caption font-medium uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="px-3 py-2">What was paid for</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(s.lines ?? []).map((l) => (
                  <tr key={l.id}>
                    <td className="px-3 py-2">
                      <span className="text-neutral-900">{l.label}</span>
                      <span className="block text-caption text-neutral-500">
                        {BILLABLE_EVENT_LABEL[l.event] ?? l.event}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{l.quantity}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatPaise(l.ratePaise, s.currency)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatPaise(l.amountPaise, s.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <dl className="space-y-1 text-sm">
            <div className="flex justify-between"><dt>Statement total</dt><dd className="tabular-nums">{formatPaise(s.subtotalPaise, s.currency)}</dd></div>
            <div className="flex justify-between"><dt>Paid</dt><dd className="tabular-nums">{formatPaise(s.paidPaise, s.currency)}</dd></div>
            <div className="flex justify-between border-t border-neutral-200 pt-1 font-semibold">
              <dt>Outstanding</dt><dd className="tabular-nums">{formatPaise(s.outstandingPaise, s.currency)}</dd>
            </div>
          </dl>

          {(s.payments ?? []).length > 0 && (
            <div>
              <div className="text-small font-medium text-neutral-800">Payments</div>
              <ul className="mt-1 space-y-1">
                {(s.payments ?? []).map((pay) => (
                  <li key={pay.id} className="flex items-center justify-between text-xs text-neutral-700">
                    <span>
                      {formatDate(pay.paidOn)} · {pay.method.replace(/_/g, ' ').toLowerCase()}
                      {pay.reference ? ` · ${pay.reference}` : ''}
                      {pay.reversesId ? ' · reversal' : ''}
                    </span>
                    <span className="tabular-nums">{formatPaise(pay.amountPaise, s.currency)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            {may.reverse && s.status !== 'VOID' && s.status !== 'PAID' && (
              <Button variant="secondary" className="border-danger-600/40 text-danger-600 hover:bg-danger-100" onClick={() => setVoiding(true)}>
                Void…
              </Button>
            )}
            {may.issue && s.status === 'DRAFT' && (
              <Button onClick={() => setIssuing(true)}>
                Issue to payee…
              </Button>
            )}
            {may.record && (s.status === 'ISSUED' || s.status === 'PARTIALLY_PAID') && (
              <Button onClick={() => setPaying(true)}>Record payment</Button>
            )}
          </div>
        </div>
      )}

      {paying && s && (
        <RecordPaymentDialog
          settlement={s}
          onClose={() => setPaying(false)}
          onDone={() => { setPaying(false); statement.reload(); onChanged(); }}
        />
      )}
      {issuing && s && (
        <ConfirmDialog
          title={`Issue ${s.reference}?`}
          what={<>The statement is sent to the payee for <strong>{formatPaise(s.subtotalPaise, s.currency)}</strong>.</>}
          affects="Its figures stay frozen. Payments can be recorded against it once it is issued."
          irreversible="An issued statement is never deleted. A mistake is corrected by voiding it and closing the period again."
          confirmLabel="Issue statement"
          onConfirm={async () => {
            await api.post(`/settlements/${s.id}/issue`, {});
            setIssuing(false);
            statement.reload();
            onChanged();
          }}
          onCancel={() => setIssuing(false)}
        />
      )}
      {voiding && s && (
        <VoidDialog
          settlement={s}
          onClose={() => setVoiding(false)}
          onDone={() => { setVoiding(false); statement.reload(); onChanged(); }}
        />
      )}
    </Dialog>
  );
}

function RecordPaymentDialog({ settlement, onClose, onDone }: {
  settlement: SettlementRow; onClose: () => void; onDone: () => void;
}) {
  const [rupees, setRupees] = useState((settlement.outstandingPaise / 100).toFixed(2));
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paise = Math.round(Number(rupees) * 100);
  const valid = paise > 0 && /^\d{4}-\d{2}-\d{2}$/.test(paidOn);
  const over = paise > settlement.outstandingPaise;

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/settlements/${settlement.id}/payments`, {
        amountPaise: paise, paidOn, method,
        reference: reference.trim() || undefined,
      }, crypto.randomUUID());
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Record payment — ${settlement.reference}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-neutral-500">
          Money that has actually moved. Payments are append-only: a mistake is corrected by a
          reversal that points at what it reverses, never by deleting the row.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="Amount (₹)" inputMode="decimal" value={rupees} required
            onChange={(v) => setRupees(v.replace(/[^0-9.]/g, ''))}
            error={over ? 'This is more than the statement still owes.' : null}
          />
          <TextField label="Paid on" type="date" value={paidOn} onChange={setPaidOn} required />
          <label className="block">
            <span className="text-small font-medium text-neutral-800">Method</span>
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="BANK_TRANSFER">Bank transfer</option>
              <option value="UPI">UPI</option>
              <option value="CHEQUE">Cheque</option>
              <option value="CASH">Cash</option>
              <option value="OTHER">Other</option>
            </Select>
          </label>
          <label className="block">
            <span className="text-small font-medium text-neutral-800">Bank reference (optional)</span>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / cheque number" />
          </label>
        </div>
        <p className="text-xs text-neutral-500">
          Outstanding on this statement: <span className="tabular-nums">{formatPaise(settlement.outstandingPaise, settlement.currency)}</span>
        </p>
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy} loading={busy}>Record payment</Button>
        </div>
      </div>
    </Dialog>
  );
}

function VoidDialog({ settlement, onClose, onDone }: {
  settlement: SettlementRow; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/settlements/${settlement.id}/void`, { reason: reason.trim() });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Void ${settlement.reference}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-neutral-500">
          Voiding withdraws the statement and releases the work it claimed, so the period can be
          closed again.
          <InfoTip label="About voiding" className="ml-1">
            The statement itself is kept — one that was sent and then vanished is worse
            than one marked withdrawn.
          </InfoTip>
        </p>
        <TextField
          label="Why is this statement being voided?" value={reason} onChange={setReason} required autoFocus
          placeholder="Read months later by somebody who was not in the room"
          hint="At least 10 characters. Recorded against your name."
          error={reason.trim().length > 0 && reason.trim().length < 10
            ? `${10 - reason.trim().length} more character${10 - reason.trim().length === 1 ? '' : 's'} needed.`
            : null}
        />
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={reason.trim().length < 10 || busy} loading={busy}>Void statement</Button>
        </div>
      </div>
    </Dialog>
  );
}
